import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Info } from "lucide-react";
import React, { useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import BuildkiteIcon from "src/components/ui/icons/buildkite";
import GithubIcon from "src/components/ui/icons/github";
import { Link } from "src/components/ui/link";
import { SimpleDataTable } from "src/components/ui/SimpleDataTable";
import { StatusBadge } from "src/components/ui/StatusBadge";
import { Tooltip } from "src/components/ui/tooltip";
import { useMe, usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getProductName } from "src/utils/brandTheme";

import type { CapabilitiesWorkspaceQuery } from "./__generated__/CapabilitiesWorkspaceQuery.graphql";
import type { CapabilitiesWorkspaceUpdateMutation } from "./__generated__/CapabilitiesWorkspaceUpdateMutation.graphql";
import CapabilitiesEmptyState from "./CapabilitiesEmptyState";
import { NewCapabilityWizard } from "./NewCapabilityWizard";

const APPROVAL_STYLE_LABELS: Record<string, string> = {
  AUTOMATIC: "Automatic",
  MANUAL: "Manual",
};

const capabilitiesWorkspaceQuery = graphql`
  query CapabilitiesWorkspaceQuery {
    me {
      ...appMe
      id
      organization {
        id
        capabilities {
          edges {
            node {
              id
              name
              description
              capabilityType
              isActive
              approvalStyle
              approvalRate30d
              executions(limit: 1, sortBy: "created_at", sortOrder: "desc") {
                edges {
                  node {
                    id
                    createdAt
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const updateCapabilityMutation = graphql`
  mutation CapabilitiesWorkspaceUpdateMutation($input: UpdateCapabilityInput!) {
    updateCapability(input: $input) {
      capability {
        id
        name
        isActive
      }
      error
    }
  }
`;

export function CapabilitiesWorkspace() {
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { toast } = useToast();
  const { hasScope } = usePermissions();
  const me = useMe();
  const brandTheme = me.organization?.brandTheme ?? "TIERZERO";

  const data = useLazyLoadQuery<CapabilitiesWorkspaceQuery>(
    capabilitiesWorkspaceQuery,
    {},
    { fetchPolicy: "network-only", fetchKey: refreshKey }
  );

  const [commitUpdate, isUpdating] = useMutation<CapabilitiesWorkspaceUpdateMutation>(updateCapabilityMutation);

  type CapabilityNode = NonNullable<
    NonNullable<CapabilitiesWorkspaceQuery["response"]["me"]["organization"]["capabilities"]["edges"][number]>["node"]
  >;

  const capabilities = useMemo<CapabilityNode[]>(() => {
    const edges = data.me.organization.capabilities.edges ?? [];
    return edges.map((edge) => edge.node).filter((node): node is CapabilityNode => Boolean(node));
  }, [data.me.organization.capabilities.edges]);

  const handleToggleActive = React.useCallback(
    (capabilityId: string, currentIsActive: boolean) => {
      commitUpdate({
        variables: {
          input: {
            id: capabilityId,
            isActive: !currentIsActive,
          },
        },
        onCompleted: (response) => {
          if (response.updateCapability.error) {
            toast({
              title: "Error",
              description: response.updateCapability.error,
              variant: "error",
            });
          } else if (response.updateCapability.capability?.name) {
            toast({
              title: `Capability ${!currentIsActive ? "activated" : "deactivated"}`,
              description: `"${response.updateCapability.capability.name}" has been ${!currentIsActive ? "activated" : "deactivated"} successfully.`,
              variant: "success",
            });
            setRefreshKey((prev) => prev + 1);
          }
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: error.message,
            variant: "error",
          });
        },
      });
    },
    [commitUpdate, toast]
  );

  const formatTimestamp = React.useCallback((value: unknown) => {
    if (typeof value !== "string") {
      return "Never";
    }

    const createdAtDate = new Date(value);
    const utcDate = new Date(createdAtDate.getTime() + createdAtDate.getTimezoneOffset() * 60000);
    const utcTime = format(utcDate, "MMM d, HH:mm");
    const localTime = format(createdAtDate, "MMM d, HH:mm");

    return (
      <span>
        {utcTime} UTC <span className="text-xs text-muted-foreground">({localTime} Local)</span>
      </span>
    );
  }, []);

  const columns = useMemo<ColumnDef<CapabilityNode>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <Link
            to={`/capabilities/${row.original.id}`}
            className="text-sm text-primary transition-colors hover:text-primary/80 hover:no-underline"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: "status",
        accessorFn: (row) => (row.isActive ? "ACTIVE" : "INACTIVE"),
        header: "Status",
        cell: ({ row }) => {
          const isActive = row.original.isActive;
          const canWrite = hasScope("write:capabilities");

          return (
            <StatusBadge
              label={isActive ? "ACTIVE" : "INACTIVE"}
              variant={isActive ? "active" : "inactive"}
              readOnly={!canWrite}
              disabled={isUpdating}
              onClick={canWrite ? () => handleToggleActive(row.original.id, isActive) : undefined}
              title={`Click to ${isActive ? "deactivate" : "activate"} capability`}
              showToggleIcon
            />
          );
        },
      },
      {
        id: "type",
        accessorFn: (row) => row.capabilityType,
        header: "Type",
        cell: ({ row }) => (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {row.original.capabilityType === "GITHUB_ACTIONS_WORKFLOW" && (
              <>
                <GithubIcon className="h-5 w-5 text-muted-foreground" />
                <span>Github</span>
              </>
            )}
            {row.original.capabilityType === "BUILDKITE_PIPELINE" && (
              <>
                <BuildkiteIcon className="h-5 w-5 text-muted-foreground" />
                <span>Buildkite</span>
              </>
            )}
          </div>
        ),
      },
      {
        id: "approvalType",
        accessorFn: (row) => row.approvalStyle,
        header: "Approval Type",
        meta: { headerClassName: "whitespace-nowrap" },
        cell: ({ row }) => (
          <div className="text-sm text-muted-foreground">
            {APPROVAL_STYLE_LABELS[row.original.approvalStyle ?? "MANUAL"] || row.original.approvalStyle || "Manual"}
          </div>
        ),
      },
      {
        id: "lastCalled",
        accessorFn: (row) => {
          const edges = row.executions?.edges ?? [];
          if (edges.length === 0) return null;
          return edges[0]?.node?.createdAt ?? null;
        },
        header: "Last Called",
        meta: {
          headerClassName: "whitespace-nowrap",
          cellClassName: "text-sm text-muted-foreground whitespace-nowrap",
          formatCell: (value) => formatTimestamp(value),
        },
      },
    ],
    [formatTimestamp, handleToggleActive, isUpdating, hasScope]
  );

  const handleModalClose = React.useCallback((created?: boolean) => {
    setIsNewModalOpen(false);
    if (created) {
      setRefreshKey((prev) => prev + 1);
    }
  }, []);

  const tooltipDescription = `Configure custom actions that ${getProductName(brandTheme)} can perform such as deployments, rollbacks, and reverts.`;

  const hasNoCapabilities = capabilities.length === 0;

  if (hasNoCapabilities) {
    return (
      <div className="h-full w-full overflow-y-auto custom-scrollbar">
        <CapabilitiesEmptyState onCreateClick={() => setIsNewModalOpen(true)} />
        <NewCapabilityWizard isOpen={isNewModalOpen} onClose={handleModalClose} />
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto custom-scrollbar">
      <div className="pl-6 pr-6 pt-4 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <h1 className="text-xl font-medium text-foreground">Capabilities</h1>
            <Tooltip
              content={tooltipDescription}
              position="right"
              maxWidth="16rem"
            >
              <button className="text-muted-foreground hover:text-foreground transition-colors">
                <Info className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
          <Tooltip
            content={!hasScope("write:capabilities") ? "You don't have permission to create capabilities" : null}
            position="top"
          >
            <span className="inline-block">
              <Button
                size="sm"
                onClick={() => setIsNewModalOpen(true)}
                disabled={!hasScope("write:capabilities")}
              >
                Create Capability
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
      <div className="px-6 pb-6">
        <div className="space-y-3">
          <SimpleDataTable
            data={capabilities}
            columns={columns}
            enableSorting
            enableCellFormatting
            getRowId={(row) => row.id}
            emptyState={
              <span className="text-muted-foreground">No capabilities configured yet. Create one to get started.</span>
            }
          />
        </div>
      </div>

      {/* New Capability Wizard */}
      <NewCapabilityWizard isOpen={isNewModalOpen} onClose={handleModalClose} />
    </div>
  );
}
